import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { HiUser, HiFilter, HiX, HiSearch } from 'react-icons/hi'
import SEO from '../../components/SEO/SEO'
import { seoBreadcrumbPair, seoBreadcrumbTriple } from '../../utils/seoUtils'
import './BlackBelts.css'

function BlackBelts() {
  const { t, i18n } = useTranslation()
  const { disciplineId } = useParams()
  const [selectedDan, setSelectedDan] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCountry, setSelectedCountry] = useState('all')
  const [selectedBi, setSelectedBi] = useState('all')
  const [selectedCity, setSelectedCity] = useState('all')
  const [modalImage, setModalImage] = useState(null)
  
  const isJapanese = i18n.language === 'ja'
  const isRussian = i18n.language === 'ru'
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767)

  const getDisciplineName = (id) => {
    const names = {
      kubo: { ru: 'КУБО', en: 'KUBO', ja: 'КУБО' },
      aikido: { ru: 'Айкидо', en: 'Aikido', ja: '合気道' },
      aikijutsu: { ru: 'Айки-дзюцу', en: 'Aiki-jutsu', ja: '合気術' },
      jujutsu: { ru: 'Дзю-дзюцу', en: 'Jujutsu', ja: '柔術' },
      aikibujutsu: { ru: 'Айки-бу-дзюцу', en: 'Aiki-bu-jutsu', ja: '合気武術' },
      karatejutsu: { ru: 'Карате-дзюцу', en: 'Karate-jutsu', ja: '空手術' },
      kenjutsu: { ru: 'Кэн-дзюцу', en: 'Kenjutsu', ja: '剣術' },
      kempo: { ru: 'Кемпо', en: 'Kempo', ja: '拳法' },
    }
    if (!id) return ''
    const lang = isJapanese ? 'ja' : isRussian ? 'ru' : 'en'
    return names[id] ? names[id][lang] : id
  }

  const breadcrumbItems = useMemo(() => {
    const home = t('common.home')
    const bb = t('common.blackBelts')
    if (!disciplineId) return seoBreadcrumbPair(home, bb, 'black-belts')
    return seoBreadcrumbTriple(home, bb, 'black-belts', getDisciplineName(disciplineId), `black-belts/${disciplineId}`)
  }, [disciplineId, t, isJapanese, isRussian])

  // Определяем мобильное устройство
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Сбрасываем фильтры при изменении языка (кроме поиска и дана)
  useEffect(() => {
    setSelectedCountry('all')
    setSelectedBi('all')
    setSelectedCity('all')
  }, [i18n.language])

  // Структура данных черных поясов по данам
  const blackBeltsByDan = {
    10: [
      {
        id: 1,
        nameRu: 'Бессонов Павел Гаврилович',
        nameEn: 'Bessonov Pavel Gavrilovich',
        nameJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        locationRu: 'Болгария',
        locationEn: 'Bulgaria',
        locationJa: 'ブルガリア',
        cityRu: 'Варна',
        cityEn: 'Varna',
        cityJa: 'ヴァルナ',
        photo: '/images/persons/Grandmaster_Bessonov_Pavel_Gavrilovich.jpg',
        certificatePhoto: null,
        certificateNumber: 'б/н',
        certificationDate: '2004',
        bi: 'айки-бу-дзюцу',
        biRu: 'айки-бу-дзюцу',
        biEn: 'Aiki-Bu-Jutsu',
        biJa: '合気武術',
        examiner: 'Высший Совет SUMA',
        examinerRu: 'Высший Совет SUMA',
        examinerEn: 'Supreme Council SUMA',
        examinerJa: 'SUMA最高評議会',
        certificationCity: 'Болгария Варна',
        certificationCityRu: 'Болгария Варна',
        certificationCityEn: 'Bulgaria Varna',
        certificationCityJa: 'ブルガリア・ヴァルナ'
      },
      {
        id: 2,
        nameRu: 'Абубакаров Сайпуди Сайдиевич',
        nameEn: 'Abubakarov Saipudi Saidievich',
        nameJa: 'アブバカロフ・サイプディ・サイディエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Грозный',
        cityEn: 'Grozny',
        cityJa: 'グロズヌイ',
        photo: null,
        certificatePhoto: '/images/certificates/АБУБАКАРОВ САЙПУДИ САЙДИЕВИЧ, ГРОЗНЫЙ, РОССИЯ.jpg',
        certificateNumber: 'a-0070',
        certificationDate: '12.02.2022',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      }
    ],
    9: [
      {
        id: 3,
        nameRu: 'Глущенко Владимир Леонидович',
        nameEn: 'Glushchenko Vladimir Leonidovich',
        nameJa: 'グルシェンコ・ウラジーミル・レオニドヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Санкт-Петербург',
        cityEn: 'Saint Petersburg',
        cityJa: 'サンクトペテルブルク',
        photo: '/images/persons/Glushchenko_Vladimir_Leonidovich.jpg',
        certificatePhoto: '/images/certificates/ГЛУЩЕНКО ВЛАДИМИР ЛЕОНИДОВИЧ, САНКТ-ПЕТЕРБУРГ, РОССИЯ.jpg',
        certificateNumber: 'g-0073-23',
        certificationDate: '23.04.2023',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 4,
        nameRu: 'Фильков Владимир Григорьевич',
        nameEn: 'Filkov Vladimir Grigorievich',
        nameJa: 'フィルコフ・ウラジーミル・グリゴリエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Ревда',
        cityEn: 'Revda',
        cityJa: 'レヴダ',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 5,
        nameRu: 'Аллахвердиев Тарверди',
        nameEn: 'Allakhverdiev Tarverdi',
        nameJa: 'アッラフヴェルディエフ・タルヴェルディ',
        locationRu: 'Азербайджан',
        locationEn: 'Azerbaijan',
        locationJa: 'アゼルバイジャン',
        cityRu: 'г. Баку',
        cityEn: 'Baku',
        cityJa: 'バクー',
        photo: '/images/persons/Allakhverdiev_Tarverdi.jpg',
        certificatePhoto: '/images/certificates/АЛЛАХВЕРДИЕВ ТАРВЕРДИ БАКУ, АЗЕРБАЙДЖАН.jpg',
        certificateNumber: 'a-008',
        certificationDate: '12.04.2019',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Baku',
        certificationCityRu: 'Баку',
        certificationCityEn: 'Baku',
        certificationCityJa: 'バクー'
      }
    ],
    8: [],
    7: [
      {
        id: 6,
        nameRu: 'Шаров Владимир Георгиевич',
        nameEn: 'Sharov Vladimir Georgievich',
        nameJa: 'シャロフ・ウラジーミル・ゲオルギエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Россошь',
        cityEn: 'Rossosh',
        cityJa: 'ロッソシ',
        photo: null,
        certificatePhoto: '/images/certificates/ШАРОВ ВЛАДИМИР ГЕОРГИЕВИЧ РОССОШЬ, РОССИЯ.jpg',
        certificateNumber: 'sh-229-24',
        certificationDate: '05.12.2024',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 7,
        nameRu: 'Богданов Павел Борисович',
        nameEn: 'Bogdanov Pavel Borisovich',
        nameJa: 'ボグダノフ・パーヴェル・ボリソヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Москва',
        cityEn: 'Moscow',
        cityJa: 'モスクワ',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 8,
        nameRu: 'Усманов Фахриддин',
        nameEn: 'Usmanov Fakhriddin',
        nameJa: 'ウスマノフ・ファフリディン',
        locationRu: 'Узбекистан',
        locationEn: 'Uzbekistan',
        locationJa: 'ウズベキスタン',
        cityRu: 'г. Ташкент',
        cityEn: 'Tashkent',
        cityJa: 'タシュケント',
        photo: '/images/persons/Usmanov_Fakhriddin.jpg',
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 9,
        nameRu: 'Крылов Михаил Валентинович',
        nameEn: 'Krylov Mikhail Valentinovich',
        nameJa: 'クルイロフ・ミハイル・ヴァレンティノヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Вичуга',
        cityEn: 'Vichuga',
        cityJa: 'ヴィチュガ',
        photo: null,
        certificatePhoto: '/images/certificates/КРЫЛОВ МИХАИЛ ВАЛЕНТИНОВИЧ ВИЧУГА,РОССИЯ.jpg',
        certificateNumber: 'k-008',
        certificationDate: '07.05.2017',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Vladimir',
        certificationCityRu: 'Владимир',
        certificationCityEn: 'Vladimir',
        certificationCityJa: 'ウラジーミル'
      }
    ],
    6: [
      {
        id: 10,
        nameRu: 'Софронов Николай Викторович',
        nameEn: 'Sofronov Nikolai Viktorovich',
        nameJa: 'ソフロノフ・ニコライ・ヴィクトロヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Полевской',
        cityEn: 'Polevskoy',
        cityJa: 'ポレヴスコイ',
        photo: '/images/persons/Sofronov_Nikolay_Viktorovich.jpg',
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 11,
        nameRu: 'Дросос Вагелис',
        nameEn: 'Drosos Vagelis',
        nameJa: 'ドロソス・ヴァゲリス',
        locationRu: 'Греция',
        locationEn: 'Greece',
        locationJa: 'ギリシャ',
        cityRu: 'г. Афины',
        cityEn: 'Athens',
        cityJa: 'アテネ',
        photo: '/images/persons/Vagelis_Drosos.jpg',
        certificatePhoto: '/images/certificates/ВАГЕЛИС ДРОСОС. АФИНЫ. ГРЕЦИЯ.jpg',
        certificateNumber: 'd-003',
        certificationDate: '20.08.2018',
        bi: '—',
        biRu: '—',
        biEn: '—',
        biJa: '—',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 12,
        nameRu: 'Альжанов Даулет Исаевич',
        nameEn: 'Alzhanov Daulet Isayevich',
        nameJa: 'アルジャノフ・ダウレト・イサエヴィチ',
        locationRu: 'Казахстан',
        locationEn: 'Kazakhstan',
        locationJa: 'カザフスタン',
        cityRu: 'г. Павлодар',
        cityEn: 'Pavlodar',
        cityJa: 'パヴロダル',
        photo: '/images/persons/Alzhanov_Daulet_Isaevich.jpg',
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 13,
        nameRu: 'Носов Александр Викторович',
        nameEn: 'Nosov Alexander Viktorovich',
        nameJa: 'ノソフ・アレクサンドル・ヴィクトロヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Санкт-Петербург',
        cityEn: 'Saint Petersburg',
        cityJa: 'サンクトペテルブルク',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      }
    ],
    5: [
      {
        id: 14,
        nameRu: 'Махнёв Михаил Сергеевич',
        nameEn: 'Makhnev Mikhail Sergeevich',
        nameJa: 'マフネフ・ミハイル・セルゲエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Екатеринбург',
        cityEn: 'Ekaterinburg',
        cityJa: 'エカテリンブルク',
        photo: '/images/persons/Makhnev_Mikhail_Sergeevich.jpg',
        certificatePhoto: '/images/certificates/МАХНЕВ МИХАИЛ СЕРГЕЕВИЧ, ПЕРВОУРАЛЬСК, РОССИЯ.webp',
        certificateNumber: 's-0121-24',
        certificationDate: '5.01.2024',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベッソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg, Russia',
        certificationCityRu: 'Екатеринбург, Россия',
        certificationCityEn: 'Ekaterinburg, Russia',
        certificationCityJa: 'エカテリンブルク、ロシア'
      },
      {
        id: 15,
        nameRu: 'Ножкин Александр Николаевич',
        nameEn: 'Nozhkin Alexander Nikolaevich',
        nameJa: 'ノジキン・アレクサンドル・ニコラエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Санкт-Петербург',
        cityEn: 'Saint Petersburg',
        cityJa: 'サンクトペテルブルク',
        photo: null,
        certificatePhoto: '/images/certificates/НОЖКИН АЛЕКСАНДР НИКОЛАЕВИЧ, САНКТ- ПЕТЕРБУРГ.jpg',
        certificateNumber: 'n-115-22',
        certificationDate: '06.10.2022',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 16,
        nameRu: 'Беляков Дмитрий',
        nameEn: 'Belyakov Dmitry',
        nameJa: 'ベリャコフ・ドミトリー',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Иваново',
        cityEn: 'Ivanovo',
        cityJa: 'イヴァノヴォ',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 17,
        nameRu: 'Фомичёв Дмитрий Владимирович',
        nameEn: 'Fomichev Dmitry Vladimirovich',
        nameJa: 'フォミチェフ・ドミトリー・ウラジーミロヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Ковров',
        cityEn: 'Kovrov',
        cityJa: 'コヴロフ',
        photo: null,
        certificatePhoto: '/images/certificates/ФОМИЧЁВ ДМИТРИЙ ВЛАДИМИРОВИЧ КОВРОВ,РОССИЯ.jpg',
        certificateNumber: 'f-064',
        certificationDate: '13.03.2022',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 18,
        nameRu: 'Алиев Вахид',
        nameEn: 'Aliev Vakhid',
        nameJa: 'アリエフ・ヴァヒド',
        locationRu: 'Азербайджан',
        locationEn: 'Azerbaijan',
        locationJa: 'アゼルバイジャン',
        cityRu: 'г. Баку',
        cityEn: 'Baku',
        cityJa: 'バクー',
        photo: '/images/persons/Aliev_Vakhid.jpg',
        certificatePhoto: '/images/certificates/АЛИЕВ ВАХИД БАКУ, АЗЕРБАЙДЖАН.jpg',
        certificateNumber: 'a-025',
        certificationDate: '21.11.2020',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 19,
        nameRu: 'Бурков Александр Геннадьевич',
        nameEn: 'Burkov Alexander Gennadievich',
        nameJa: 'ブルコフ・アレクサンドル・ゲンナディエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Ковров',
        cityEn: 'Kovrov',
        cityJa: 'コヴロフ',
        photo: null,
        certificatePhoto: '/images/certificates/БУРКОВ АЛЕКСАНДР ГЕННАДЬЕВИЧ КОВРОВ, РОССИЯ.jpg',
        certificateNumber: 'b-008',
        certificationDate: '10.03.2014',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Vladimir',
        certificationCityRu: 'Владимир',
        certificationCityEn: 'Vladimir',
        certificationCityJa: 'ウラジーミル'
      },
      {
        id: 20,
        nameRu: 'Ляшенко Ярослав Юрьевич',
        nameEn: 'Lyashenko Yaroslav Yurievich',
        nameJa: 'リャシェンコ・ヤロスラフ・ユリエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Ростов-на-Дону',
        cityEn: 'Rostov-on-Don',
        cityJa: 'ロストフ・ナ・ドヌ',
        photo: null,
        certificatePhoto: '/images/certificates/ЛЯШЕНКО ЯРОСЛАВ ЮРЬЕВИЧ РОСТОВ НА ДОНУ, РОССИЯ.jpg',
        certificateNumber: 'L-004',
        certificationDate: '01.09.2018',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      }
    ],
    4: [
      {
        id: 21,
        nameRu: 'Комаров Антон Валерьевич',
        nameEn: 'Komarov Anton Valeryevich',
        nameJa: 'コマロフ・アントン・ヴァレリエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Первоуральск',
        cityEn: 'Pervouralsk',
        cityJa: 'ペルヴォウラリスク',
        photo: '/images/persons/Komarov_Anton_Valerevich.jpg',
        certificatePhoto: '/images/certificates/КОМАРОВ АНТОН, РОССИЯ.jpg',
        certificateNumber: 'k-081-24',
        certificationDate: '-',
        bi: 'Aiki-Bu-Jutsu',
        biRu: 'Айки-бу-дзюцу',
        biEn: 'Aiki-Bu-Jutsu',
        biJa: '合気武術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: '-',
        certificationCityRu: '-',
        certificationCityEn: '-',
        certificationCityJa: '-'
      },
      {
        id: 22,
        nameRu: 'Яхнюк Сергей Васильевич',
        nameEn: 'Yakhnyuk Sergey Vasilievich',
        nameJa: 'ヤフニュク・セルゲイ・ヴァシリエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Санкт-Петербург',
        cityEn: 'Saint Petersburg',
        cityJa: 'サンクトペテルブルク',
        photo: null,
        certificatePhoto: '/images/certificates/ЯХНЮК СЕРЕГЕЙ ВАСИЛЬЕВИЧ, САНКТ-ПЕТЕРБУРГ, РОССИЯ.jpg',
        certificateNumber: 'ya-041',
        certificationDate: '27.06.2021',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 23,
        nameRu: 'Беседин Игорь Николаевич',
        nameEn: 'Besedin Igor Nikolaevich',
        nameJa: 'ベセディン・イーゴリ・ニコラエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Новороссийск',
        cityEn: 'Novorossiysk',
        cityJa: 'ノヴォロシースク',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 24,
        nameRu: 'Хасанов Хамзат Ибрагимович',
        nameEn: 'Khasanov Khamzat Ibragimovich',
        nameJa: 'ハサノフ・ハムザト・イブラギモヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Грозный',
        cityEn: 'Grozny',
        cityJa: 'グロズヌイ',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 25,
        nameRu: 'Назаров Павел Павлович',
        nameEn: 'Nazarov Pavel Pavlovich',
        nameJa: 'ナザロフ・パーヴェル・パヴロヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Ковров',
        cityEn: 'Kovrov',
        cityJa: 'コヴロフ',
        photo: null,
        certificatePhoto: '/images/certificates/НАЗАРОВ ПАВЕЛ ПАВЛОВИЧ КОВРОВ, РОССИЯ.jpeg',
        certificateNumber: 'S-017',
        certificationDate: '05.02.2016',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Kovrov',
        certificationCityRu: 'Ковров',
        certificationCityEn: 'Kovrov',
        certificationCityJa: 'コヴロフ'
      },
      {
        id: 26,
        nameRu: 'Нестеров Александр Александрович',
        nameEn: 'Nesterov Alexander Alexandrovich',
        nameJa: 'ネステロフ・アレクサンドル・アレクサンドロヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Санкт-Петербург',
        cityEn: 'Saint Petersburg',
        cityJa: 'サンクトペテルブルク',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 27,
        nameRu: 'Штембуляк Юрий Анатольевич',
        nameEn: 'Shtembulyak Yuri Anatolievich',
        nameJa: 'シテンブリャク・ユーリ・アナトリエヴィチ',
        locationRu: 'Украина',
        locationEn: 'Ukraine',
        locationJa: 'ウクライナ',
        cityRu: 'г. Одесса',
        cityEn: 'Odessa',
        cityJa: 'オデッサ',
        photo: null,
        certificatePhoto: '/images/certificates/ШТЕМБУЛЯК ЮРИЙ АНАТОЛЬЕВИЧ ОДЕССА, УКРАИНА.jpg',
        certificateNumber: 'sh-003',
        certificationDate: '17.05.2014',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Varna',
        certificationCityRu: 'Варна',
        certificationCityEn: 'Varna',
        certificationCityJa: 'ヴァルナ'
      },
      {
        id: 28,
        nameRu: 'Михайлов Красимир Димитров',
        nameEn: 'Mikhailov Krasimir Dimitrov',
        nameJa: 'ミハイロフ・クラシミル・ディミトロフ',
        locationRu: 'Болгария',
        locationEn: 'Bulgaria',
        locationJa: 'ブルガリア',
        cityRu: 'г. Варна',
        cityEn: 'Varna',
        cityJa: 'ヴァルナ',
        photo: '/images/persons/Dimitrov_Krasimir_Mihailov.jpg',
        certificatePhoto: '/images/certificates/КРАСИМИР ДИМИТРОВ МИХАЙЛОВ ВАРНА, БОЛГАРИЯ.jpg',
        certificateNumber: 'd-014',
        certificationDate: '10.05.2014',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Varna',
        certificationCityRu: 'Варна',
        certificationCityEn: 'Varna',
        certificationCityJa: 'ヴァルナ'
      }
    ],
    3: [
      {
        id: 29,
        nameRu: 'Стяжкин Андрей Геннадьевич',
        nameEn: 'Styazhkin Andrey Gennadievich',
        nameJa: 'スチャジキン・アンドレイ・ゲンナディエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Нижний Тагил',
        cityEn: 'Nizhny Tagil',
        cityJa: 'ニジニ・タギル',
        photo: null,
        certificatePhoto: '/images/certificates/СТЯЖКИН АНДРЕЙ ГЕННАДЬЕВИЧ.jpg',
        certificateNumber: 's-201-25',
        certificationDate: '04.01.2025',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Sysert',
        certificationCityRu: 'Сысерть',
        certificationCityEn: 'Sysert',
        certificationCityJa: 'シセルト'
      },
      {
        id: 30,
        nameRu: 'Поспеев Руслан Валентинович',
        nameEn: 'Pospеev Ruslan Valentinovich',
        nameJa: 'ポスペエフ・ルスラン・ヴァレンティノヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Каменск-Уральский',
        cityEn: 'Kamensk-Uralsky',
        cityJa: 'カメンスク・ウラルスキー',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 31,
        nameRu: 'Черепанов Иван Евгеньевич',
        nameEn: 'Cherepanov Ivan Evgenievich',
        nameJa: 'チェレパノフ・イヴァン・エヴゲニエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Сысерть',
        cityEn: 'Sysert',
        cityJa: 'シセルト',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 32,
        nameRu: 'Абдулаев Роман Турсунович',
        nameEn: 'Abdulaev Roman Tursunovich',
        nameJa: 'アブドゥラエフ・ロマン・トゥルスノヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Первоуральск',
        cityEn: 'Pervouralsk',
        cityJa: 'ペルヴォウラリスク',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 33,
        nameRu: 'Кадочникова Анастасия Александровна',
        nameEn: 'Kadochnikova Anastasia Alexandrovna',
        nameJa: 'カドチニコヴァ・アナスタシア・アレクサンドロヴナ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Сысерть',
        cityEn: 'Sysert',
        cityJa: 'シセルト',
        photo: null,
        certificatePhoto: '/images/certificates/КАДОЧНИКОВА АНАСТАСИЯ АЛЕКСАНДРОВНА. г. СЫСЕРТЬ. РОССИЯ..jpg',
        certificateNumber: 'k-101-22',
        certificationDate: '29.07.2022',
        bi: 'Aiki-Bu-Jutsu',
        biRu: 'Айки-бу-дзюцу',
        biEn: 'Aiki-Bu-Jutsu',
        biJa: '合気武術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'The Black River',
        certificationCityRu: 'Чёрная Речка',
        certificationCityEn: 'The Black River',
        certificationCityJa: 'チョールナヤ・レチカ'
      },
      {
        id: 34,
        nameRu: 'Манчик Леонид Зусиевич',
        nameEn: 'Manchik Leonid Zusievich',
        nameJa: 'マンチク・レオニド・ズシエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Санкт-Петербург',
        cityEn: 'Saint Petersburg',
        cityJa: 'サンクトペテルブルク',
        photo: null,
        certificatePhoto: '/images/certificates/МАНЧИК ЛЕОНИД ЗУСИЕВИЧ,  САНКТ-ПЕТЕРБУРГ, РОССИЯ.jpg',
        certificateNumber: 'm-020',
        certificationDate: '05.12.2019',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      }
    ],
    2: [
      {
        id: 35,
        nameRu: 'Удинцев Михаил Владимирович',
        nameEn: 'Udintsev Mikhail Vladimirovich',
        nameJa: 'ウディンツェフ・ミハイル・ウラジーミロヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Ирбит',
        cityEn: 'Irbit',
        cityJa: 'イルビト',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 36,
        nameRu: 'Комаров Антон Валерьевич',
        nameEn: 'Komarov Anton Valeryevich',
        nameJa: 'コマロフ・アントン・ヴァレリエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Первоуральск',
        cityEn: 'Pervouralsk',
        cityJa: 'ペルヴォウラリスク',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 37,
        nameRu: 'Богомолов Павел Сергеевич',
        nameEn: 'Bogomolov Pavel Sergeevich',
        nameJa: 'ボゴモロフ・パーヴェル・セルゲエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Первоуральск',
        cityEn: 'Pervouralsk',
        cityJa: 'ペルヴォウラリスク',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 38,
        nameRu: 'Курдян Владимир Акопович',
        nameEn: 'Kurdian Vladimir Akopovich',
        nameJa: 'クルディアン・ウラジーミル・アコポヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Нижний Тагил',
        cityEn: 'Nizhny Tagil',
        cityJa: 'ニジニ・タギル',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 39,
        nameRu: 'Шаров Владимир',
        nameEn: 'Sharov Vladimir',
        nameJa: 'シャロフ・ウラジーミル',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: null,
        cityEn: null,
        cityJa: null,
        photo: null,
        certificatePhoto: '/images/certificates/ШАРОВ ВЛАДИМИР.jpg',
        certificateNumber: 'sh-103-22',
        certificationDate: '12.08.2022',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович, Vladimir Sharov (6-th DAN)',
        examinerRu: 'Бессонов Павел Гаврилович, Шаров Владимир (6 Дан)',
        examinerEn: 'Bessonov Pavel Gavrilovich, Vladimir Sharov (6th Dan)',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ、シャロフ・ウラジーミル（6段）',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 40,
        nameRu: 'Лагуткина Анна',
        nameEn: 'Lagutkina Anna',
        nameJa: 'ラグトキナ・アンナ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Россошь',
        cityEn: 'Rossosh',
        cityJa: 'ロッソシ',
        photo: null,
        certificatePhoto: '/images/certificates/ЛАГУТКИНА АННА, РОССОШЬ, РОССИЯ.jpg',
        certificateNumber: 'L-102-22',
        certificationDate: '12.08.2022',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович, Vladimir Sharov (6-th DAN)',
        examinerRu: 'Бессонов Павел Гаврилович, Шаров Владимир (6 Дан)',
        examinerEn: 'Bessonov Pavel Gavrilovich, Vladimir Sharov (6th Dan)',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ、シャロフ・ウラジーミル（6段）',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 41,
        nameRu: 'Абубакаров Магомед',
        nameEn: 'Abubakarov Magomed',
        nameJa: 'アブバカロフ・マゴメド',
        locationRu: 'Дания',
        locationEn: 'Denmark',
        locationJa: 'デンマーク',
        cityRu: null,
        cityEn: null,
        cityJa: null,
        photo: null,
        certificatePhoto: '/images/certificates/АБУБАКАРОВ МАГОМЕД, ДАНИЯ.jpeg',
        certificateNumber: 'a-062',
        certificationDate: '04.01.2022',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 42,
        nameRu: 'Пеннер Людмила',
        nameEn: 'Penner Lyudmila',
        nameJa: 'ペンナー・リュドミラ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Полевской',
        cityEn: 'Polevskoy',
        cityJa: 'ポレヴスコイ',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 43,
        nameRu: 'Немченко Юрий Владимирович',
        nameEn: 'Nemchenko Yuri Vladimirovich',
        nameJa: 'ネムチェンコ・ユーリ・ウラジーミロヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Россошь, Воронежская область',
        cityEn: 'Rossosh, Voronezh Region',
        cityJa: 'ロッソシ、ヴォロネジ州',
        photo: null,
        certificatePhoto: '/images/certificates/НЕМЧЕНКО ЮРИЙ ВЛАДИМИРОВИЧ. РОССОШЬ ВОРОНЕЖСКАЯ ОБЛ.jpg',
        certificateNumber: 'n-011',
        certificationDate: '24.10.2019',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 44,
        nameRu: 'Серов Валерий Викторович',
        nameEn: 'Serov Valery Viktorovich',
        nameJa: 'セロフ・ヴァレリー・ヴィクトロヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Вичуга, Ивановская область',
        cityEn: 'Vichuga, Ivanovo Region',
        cityJa: 'ヴィチュガ、イヴァノヴォ州',
        photo: null,
        certificatePhoto: '/images/certificates/СЕРОВ ВАЛЕРИЙ ВИКТОРОВИЧ ВИЧУГА, ИВАНОВСКАЯ ОБЛ..jpg',
        certificateNumber: 'S-009',
        certificationDate: '10.04.2016',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Vladimir',
        certificationCityRu: 'Владимир',
        certificationCityEn: 'Vladimir',
        certificationCityJa: 'ウラジーミル'
      },
      {
        id: 45,
        nameRu: 'Качалов Алексей Юрьевич',
        nameEn: 'Kachalov Alexey Yurievich',
        nameJa: 'カチャロフ・アレクセイ・ユリエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Москва',
        cityEn: 'Moscow',
        cityJa: 'モスクワ',
        photo: null,
        certificatePhoto: '/images/certificates/КАЧАЛОВ АЛЕКСЕЙ ЮРЬЕВИЧ, МОСКВА, РОССИЯ.jpg',
        certificateNumber: 'K-001',
        certificationDate: '10.01.2013',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Vladimir',
        certificationCityRu: 'Владимир',
        certificationCityEn: 'Vladimir',
        certificationCityJa: 'ウラジーミル'
      },
      {
        id: 46,
        nameRu: 'Евсенин Владимир Игоревич',
        nameEn: 'Evsenin Vladimir Igorevich',
        nameJa: 'エヴセニン・ウラジーミル・イゴレヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Екатеринбург',
        cityEn: 'Ekaterinburg',
        cityJa: 'エカテリンブルク',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      }
    ],
    1: [
      {
        id: 47,
        nameRu: 'Дадаев Абдула',
        nameEn: 'Dadaev Abdula',
        nameJa: 'ダダエフ・アブドゥラ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Грозный',
        cityEn: 'Grozny',
        cityJa: 'グロズヌイ',
        photo: null,
        certificatePhoto: '/images/certificates/ДАДАЕВ АБДУЛА. г. ГРОЗНЫЙ. РОССИЯ..jpg',
        certificateNumber: 'D-0023-24',
        certificationDate: '13.10.2024',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 48,
        nameRu: 'Алексенко Артём',
        nameEn: 'Aleksenko Artyom',
        nameJa: 'アレクセンコ・アルチョム',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Россошь',
        cityEn: 'Rossosh',
        cityJa: 'ロッソシ',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 49,
        nameRu: 'Комаров Антон Валерьевич',
        nameEn: 'Komarov Anton Valeryevich',
        nameJa: 'コマロフ・アントン・ヴァレリエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Первоуральск',
        cityEn: 'Pervouralsk',
        cityJa: 'ペルヴォウラリスク',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 50,
        nameRu: 'Алфёров Сергей',
        nameEn: 'Alferov Sergey',
        nameJa: 'アルフェロフ・セルゲイ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Россошь',
        cityEn: 'Rossosh',
        cityJa: 'ロッソシ',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 51,
        nameRu: 'Коптяков Михаил',
        nameEn: 'Koptyakov Mikhail',
        nameJa: 'コプチャコフ・ミハイル',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Сысерть',
        cityEn: 'Sysert',
        cityJa: 'シセルト',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 52,
        nameRu: 'Васильев Руслан Анатольевич',
        nameEn: 'Vasiliev Ruslan Anatolievich',
        nameJa: 'ヴァシリエフ・ルスラン・アナトリエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: null,
        cityEn: null,
        cityJa: null,
        photo: null,
        certificatePhoto: '/images/certificates/ВАСИЛЬЕВ РУСЛАН АНАТОЛЬЕВИЧ.jpg',
        certificateNumber: 'sh-117-22',
        certificationDate: '13.10.2022',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 53,
        nameRu: 'Шишкин Алексей',
        nameEn: 'Shishkin Alexey',
        nameJa: 'シシキン・アレクセイ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Санкт-Петербург',
        cityEn: 'Saint Petersburg',
        cityJa: 'サンクトペテルブルク',
        photo: null,
        certificatePhoto: '/images/certificates/ШИШКИН АЛЕКСЕЙ. САНКТ-ПЕТЕРБУРГ. РОССИЯ.jpg',
        certificateNumber: 'sh-116-22',
        certificationDate: '13.10.2022',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 54,
        nameRu: 'Поспеев Руслан Валентинович',
        nameEn: 'Pospеev Ruslan Valentinovich',
        nameJa: 'ポスペエフ・ルスラン・ヴァレンティノヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Каменск-Уральский',
        cityEn: 'Kamensk-Uralsky',
        cityJa: 'カメンスク・ウラルスキー',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 55,
        nameRu: 'Стяжкин Андрей Геннадьевич',
        nameEn: 'Styazhkin Andrey Gennadievich',
        nameJa: 'スチャジキン・アンドレイ・ゲンナディエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Нижний Тагил',
        cityEn: 'Nizhny Tagil',
        cityJa: 'ニジニ・タギル',
        photo: null,
        certificatePhoto: '/images/certificates/СТЯЖКИН АНДРЕЙ ГЕННАДЬЕВИЧ.jpg',
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 56,
        nameRu: 'Стяжкин Никита Андреевич',
        nameEn: 'Styazhkin Nikita Andreevich',
        nameJa: 'スチャジキン・ニキータ・アンドレエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Нижний Тагил',
        cityEn: 'Nizhny Tagil',
        cityJa: 'ニジニ・タギル',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 57,
        nameRu: 'Майбурд Артур Артурович',
        nameEn: 'Mayburd Artur Arturovich',
        nameJa: 'マイブルド・アルトゥル・アルトゥロヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: null,
        cityEn: null,
        cityJa: null,
        photo: null,
        certificatePhoto: '/images/certificates/МАЙБУРД АРТУР АРТУРОВИЧ.jpg',
        certificateNumber: 'm-068-22',
        certificationDate: '27.05.2022',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 58,
        nameRu: 'Витальев Лев Игоревич',
        nameEn: 'Vitaliev Lev Igorevich',
        nameJa: 'ヴィタリエフ・レフ・イゴレヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: null,
        cityEn: null,
        cityJa: null,
        photo: null,
        certificatePhoto: '/images/certificates/ВИТАЛЬЕВ ЛЕВ ИГОРЕВИЧ.jpg',
        certificateNumber: 'v-067-22',
        certificationDate: '26.05.2022',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 59,
        nameRu: 'Зыков Михаил',
        nameEn: 'Zykov Mikhail',
        nameJa: 'ズイコフ・ミハイル',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: null,
        cityEn: null,
        cityJa: null,
        photo: null,
        certificatePhoto: '/images/certificates/МИХАИЛ ЗЫКОВ.jpg',
        certificateNumber: 'z-066-22',
        certificationDate: '12.05.2022',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 60,
        nameRu: 'Толмаков Артём Алексеевич',
        nameEn: 'Tolmakov Artyom Alekseevich',
        nameJa: 'トルマコフ・アルチョム・アレクセエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Санкт-Петербург',
        cityEn: 'Saint Petersburg',
        cityJa: 'サンクトペテルブルク',
        photo: null,
        certificatePhoto: '/images/certificates/ТОЛМАКОВ АРТЕМ АЛЕКСЕЕВИЧ. САНКТ-ПЕТЕРБУРГ. РОССИЯ.jpg',
        certificateNumber: 'a-063',
        certificationDate: '23.02.2022',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 61,
        nameRu: 'Анкудинов Роман',
        nameEn: 'Ankudinov Roman',
        nameJa: 'アンクディノフ・ロマン',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Каменск-Уральский',
        cityEn: 'Kamensk-Uralsky',
        cityJa: 'カメンスク・ウラルスキー',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 62,
        nameRu: 'Виноградов Иван Дмитриевич',
        nameEn: 'Vinogradov Ivan Dmitrievich',
        nameJa: 'ヴィノグラドフ・イヴァン・ドミトリエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Санкт-Петербург',
        cityEn: 'Saint Petersburg',
        cityJa: 'サンクトペテルブルク',
        photo: null,
        certificatePhoto: '/images/certificates/ИВАН ДМИТРИЕВИЧ ВИНОГРАДОВ. САНКТ-ПЕРЕРБУРГ. РОССИЯ.jpg',
        certificateNumber: 'v-059',
        certificationDate: '14.11.2021',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 63,
        nameRu: 'Закиров Абдумалик',
        nameEn: 'Zakirov Abdumalik',
        nameJa: 'ザキロフ・アブドゥマリク',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Санкт-Петербург',
        cityEn: 'Saint Petersburg',
        cityJa: 'サンクトペテルブルク',
        photo: null,
        certificatePhoto: '/images/certificates/ЗАКИРОВ АБДУМАЛИК. САНКТ-ПЕРЕРБУРГ. РОССИЯ.jpg',
        certificateNumber: 'z-041',
        certificationDate: '25.06.2021',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 64,
        nameRu: 'Хыдыров Эльнур',
        nameEn: 'Khydyrov Elnur',
        nameJa: 'ヒディロフ・エルヌル',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: null,
        cityEn: null,
        cityJa: null,
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 65,
        nameRu: 'Макаров Кирилл',
        nameEn: 'Makarov Kirill',
        nameJa: 'マカロフ・キリル',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Россошь',
        cityEn: 'Rossosh',
        cityJa: 'ロッソシ',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 66,
        nameRu: 'Витинчук Антон Геннадьевич',
        nameEn: 'Vitinchuk Anton Gennadievich',
        nameJa: 'ヴィティンチュク・アントン・ゲンナディエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Россошь, Воронежская область',
        cityEn: 'Rossosh, Voronezh Region',
        cityJa: 'ロッソシ、ヴォロネジ州',
        photo: null,
        certificatePhoto: '/images/certificates/ВИТИНЧУК АНТОН ГЕННАЬЕВИЧ. РОССОШЬ Воронежская обл. РОССИЯ.jpg',
        certificateNumber: 'v-021',
        certificationDate: '13.07.2020',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 67,
        nameRu: 'Селецкий Эдуард Борисович',
        nameEn: 'Seletsky Eduard Borisovich',
        nameJa: 'セレツキー・エドゥアルド・ボリソヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: null,
        cityEn: null,
        cityJa: null,
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 68,
        nameRu: 'Колесов Андрей Геннадьевич',
        nameEn: 'Kolesov Andrey Gennadievich',
        nameJa: 'コレソフ・アンドレイ・ゲンナディエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Балашиха',
        cityEn: 'Balashikha',
        cityJa: 'バラシハ',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 69,
        nameRu: 'Семёнова Анастасия Вадимовна',
        nameEn: 'Semyonova Anastasia Vadimovna',
        nameJa: 'セミョーノヴァ・アナスタシア・ヴァディモヴナ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Ковров',
        cityEn: 'Kovrov',
        cityJa: 'コヴロフ',
        photo: null,
        certificatePhoto: '/images/certificates/СЕМЁНОВА АНАСТАСИЯ ВАДИМОВНА КОВРОВ РОССИЯ.jpg',
        certificateNumber: 'S-017',
        certificationDate: '26.05.2017',
        bi: 'Ju-Jutsu',
        biRu: 'Дзю-дзюцу',
        biEn: 'Ju-Jutsu',
        biJa: '柔術',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Vladimir',
        certificationCityRu: 'Владимир',
        certificationCityEn: 'Vladimir',
        certificationCityJa: 'ウラジーミル'
      },
      {
        id: 70,
        nameRu: 'Шабалин Михаил Александрович',
        nameEn: 'Shabalin Mikhail Alexandrovich',
        nameJa: 'シャバリン・ミハイル・アレクサンドロヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Ковров',
        cityEn: 'Kovrov',
        cityJa: 'コヴロフ',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 71,
        nameRu: 'Кузьменко Кирилл Олегович',
        nameEn: 'Kuzmenko Kirill Olegovich',
        nameJa: 'クズメンコ・キリル・オレゴヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Ковров',
        cityEn: 'Kovrov',
        cityJa: 'コヴロフ',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 72,
        nameRu: 'Макиев Руслан Владимирович',
        nameEn: 'Makiev Ruslan Vladimirovich',
        nameJa: 'マキエフ・ルスラン・ウラジーミロヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Ковров',
        cityEn: 'Kovrov',
        cityJa: 'コヴロフ',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 73,
        nameRu: 'Бубнова Дина Александровна',
        nameEn: 'Bubnova Dina Alexandrovna',
        nameJa: 'ブブノヴァ・ディナ・アレクサンドロヴナ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Ковров',
        cityEn: 'Kovrov',
        cityJa: 'コヴロフ',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 74,
        nameRu: 'Платоненко Кирилл Геннадьевич',
        nameEn: 'Platonenko Kirill Gennadievich',
        nameJa: 'プラトネンコ・キリル・ゲンナディエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Ковров',
        cityEn: 'Kovrov',
        cityJa: 'コヴロフ',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 75,
        nameRu: 'Нагиев Фархад',
        nameEn: 'Nagiev Farkhad',
        nameJa: 'ナギエフ・ファルハド',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Ревда, Свердловская область',
        cityEn: 'Revda, Sverdlovsk Region',
        cityJa: 'レヴダ、スヴェルドロフスク州',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 76,
        nameRu: 'Черемисин Василий',
        nameEn: 'Cheremisin Vasily',
        nameJa: 'チェレミシン・ヴァシリー',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Ревда, Свердловская область',
        cityEn: 'Revda, Sverdlovsk Region',
        cityJa: 'レヴダ、スヴェルドロフスク州',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 77,
        nameRu: 'Артёмкин Станислав',
        nameEn: 'Artyomkin Stanislav',
        nameJa: 'アルチョムキン・スタニスラフ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: null,
        cityEn: null,
        cityJa: null,
        photo: null,
        certificatePhoto: '/images/certificates/АРТЕМКИН СТАНИСЛАВ.jpg',
        certificateNumber: 'a-011',
        certificationDate: '24.07.2019',
        bi: 'Aikido',
        biRu: 'Айкидо',
        biEn: 'Aikido',
        biJa: '合気道',
        examiner: 'Бессонов Павел Гаврилович',
        examinerRu: 'Бессонов Павел Гаврилович',
        examinerEn: 'Bessonov Pavel Gavrilovich',
        examinerJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
        certificationCity: 'Ekaterinburg',
        certificationCityRu: 'Екатеринбург',
        certificationCityEn: 'Ekaterinburg',
        certificationCityJa: 'エカテリンブルク'
      },
      {
        id: 78,
        nameRu: 'Зайцев Юрий Андреевич',
        nameEn: 'Zaitsev Yuri Andreevich',
        nameJa: 'ザイツェフ・ユーリ・アンドレエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Ковров',
        cityEn: 'Kovrov',
        cityJa: 'コヴロフ',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 79,
        nameRu: 'Шарипов Сулейман',
        nameEn: 'Sharipov Suleiman',
        nameJa: 'シャリポフ・スレイマン',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Косой Брод',
        cityEn: 'Kosoy Brod',
        cityJa: 'コソイ・ブロド',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 80,
        nameRu: 'Алиев Нахид',
        nameEn: 'Aliev Nakhid',
        nameJa: 'アリエフ・ナヒド',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Полевской',
        cityEn: 'Polevskoy',
        cityJa: 'ポレヴスコイ',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 81,
        nameRu: 'Кусин Семён',
        nameEn: 'Kusin Semyon',
        nameJa: 'クシン・セミョン',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Полевской',
        cityEn: 'Polevskoy',
        cityJa: 'ポレヴスコイ',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 82,
        nameRu: 'Махнёва Ксения Евгеньевна',
        nameEn: 'Makhneva Ksenia Evgenievna',
        nameJa: 'マフネヴァ・クセニア・エヴゲニエヴナ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Первоуральск',
        cityEn: 'Pervouralsk',
        cityJa: 'ペルヴォウラリスク',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 83,
        nameRu: 'Евсенина Виктория Андреевна',
        nameEn: 'Evsenina Victoria Andreevna',
        nameJa: 'エヴセニナ・ヴィクトリア・アンドレエヴナ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Екатеринбург',
        cityEn: 'Ekaterinburg',
        cityJa: 'エカテリンブルク',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 84,
        nameRu: 'Абдулаев Роман Турсунович',
        nameEn: 'Abdulaev Roman Tursunovich',
        nameJa: 'アブドゥラエフ・ロマン・トゥルスノヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Первоуральск',
        cityEn: 'Pervouralsk',
        cityJa: 'ペルヴォウラリスク',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 85,
        nameRu: 'Семин Илья Романович',
        nameEn: 'Semin Ilya Romanovich',
        nameJa: 'セミン・イリヤ・ロマノヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Первоуральск',
        cityEn: 'Pervouralsk',
        cityJa: 'ペルヴォウラリスク',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      },
      {
        id: 86,
        nameRu: 'Богомолов Павел Сергеевич',
        nameEn: 'Bogomolov Pavel Sergeevich',
        nameJa: 'ボゴモロフ・パーヴェル・セルゲエヴィチ',
        locationRu: 'Россия',
        locationEn: 'Russia',
        locationJa: 'ロシア',
        cityRu: 'г. Первоуральск',
        cityEn: 'Pervouralsk',
        cityJa: 'ペルヴォウラリスク',
        photo: null,
        certificatePhoto: null,
        certificateNumber: null,
        certificationDate: null,
        bi: null,
        examiner: null,
        certificationCity: null
      }
    ]
  }

  // Получаем список всех данов для фильтра
  const danLevels = [
    { value: 'all', labelRu: 'Все даны', labelEn: 'All Dans', labelJa: 'すべての段' },
    { value: '10', labelRu: '10 Дан', labelEn: '10th Dan', labelJa: '10段' },
    { value: '9', labelRu: '9 Дан', labelEn: '9th Dan', labelJa: '9段' },
    { value: '8', labelRu: '8 Дан', labelEn: '8th Dan', labelJa: '8段' },
    { value: '7', labelRu: '7 Дан', labelEn: '7th Dan', labelJa: '7段' },
    { value: '6', labelRu: '6 Дан', labelEn: '6th Dan', labelJa: '6段' },
    { value: '5', labelRu: '5 Дан', labelEn: '5th Dan', labelJa: '5段' },
    { value: '4', labelRu: '4 Дан', labelEn: '4th Dan', labelJa: '4段' },
    { value: '3', labelRu: '3 Дан', labelEn: '3rd Dan', labelJa: '3段' },
    { value: '2', labelRu: '2 Дан', labelEn: '2nd Dan', labelJa: '2段' },
    { value: '1', labelRu: '1 Дан', labelEn: '1st Dan', labelJa: '1段' }
  ]

  // Получаем все уникальные значения для фильтров
  const allBelts = useMemo(() => {
    return Object.values(blackBeltsByDan).flat()
  }, [])

  const uniqueCountries = useMemo(() => {
    const countries = new Set()
    allBelts.forEach(belt => {
      const country = isJapanese ? belt.locationJa : (isRussian ? belt.locationRu : belt.locationEn)
      if (country) countries.add(country)
    })
    return Array.from(countries).sort()
  }, [allBelts, isJapanese, isRussian])

  const uniqueBIs = useMemo(() => {
    const bis = new Set()
    const normalizedMap = new Map() // Для отслеживания нормализованных значений
    
    allBelts.forEach(belt => {
      const biValue = isJapanese ? (belt.biJa || belt.bi) : (isRussian ? (belt.biRu || belt.bi) : (belt.biEn || belt.bi))
      if (biValue && biValue !== '—' && biValue !== t('blackBelts.empty', '—') && !biValue.includes('представитель') && !biValue.includes('representative') && !biValue.includes('代表')) {
        const trimmed = biValue.trim()
        // Нормализуем для сравнения (приводим к нижнему регистру, убираем лишние пробелы)
        const normalizedKey = trimmed.toLowerCase().replace(/\s+/g, ' ')
        
        // Если такого нормализованного значения еще нет, сохраняем оригинальное значение с правильным регистром
        if (!normalizedMap.has(normalizedKey)) {
          normalizedMap.set(normalizedKey, trimmed)
          bis.add(trimmed)
        }
      }
    })
    return Array.from(bis).sort()
  }, [allBelts, isJapanese, isRussian, t])

  const uniqueCities = useMemo(() => {
    const cities = new Set()
    allBelts.forEach(belt => {
      const cityValue = isJapanese ? (belt.certificationCityJa || belt.certificationCity) : (isRussian ? (belt.certificationCityRu || belt.certificationCity) : (belt.certificationCityEn || belt.certificationCity))
      if (cityValue && cityValue !== '—' && cityValue !== t('blackBelts.empty', '—') && !cityValue.includes('не указан') && !cityValue.includes('not specified') && !cityValue.includes('記載なし')) {
        cities.add(cityValue)
      }
    })
    return Array.from(cities).sort()
  }, [allBelts, isJapanese, isRussian, t])

  // Функция фильтрации пояса
  const filterBelt = (belt) => {
    // Фильтр по поисковому запросу (имя)
    if (searchQuery.trim()) {
      const nameRu = (belt.nameRu || '').toLowerCase()
      const nameEn = (belt.nameEn || '').toLowerCase()
      const nameJa = (belt.nameJa || '').toLowerCase()
      const query = searchQuery.toLowerCase().trim()
      if (!nameRu.includes(query) && !nameEn.includes(query) && !nameJa.includes(query)) {
        return false
      }
    }

    // Фильтр по стране
    if (selectedCountry !== 'all') {
      const beltCountry = isJapanese ? (belt.locationJa || '') : (isRussian ? (belt.locationRu || '') : (belt.locationEn || ''))
      if (beltCountry !== selectedCountry) {
        return false
      }
    }

    // Фильтр по БИ
    if (selectedBi !== 'all') {
      const biValue = isJapanese ? (belt.biJa || belt.bi || '') : (isRussian ? (belt.biRu || belt.bi || '') : (belt.biEn || belt.bi || ''))
      if (biValue !== selectedBi) {
        return false
      }
    }

    // Фильтр по городу аттестации
    if (selectedCity !== 'all') {
      const cityValue = isJapanese ? (belt.certificationCityJa || belt.certificationCity || '') : (isRussian ? (belt.certificationCityRu || belt.certificationCity || '') : (belt.certificationCityEn || belt.certificationCity || ''))
      if (cityValue !== selectedCity) {
        return false
      }
    }

    return true
  }

  // Получаем отфильтрованные данные
  const getFilteredData = () => {
    let data = []
    
    if (selectedDan === 'all') {
      // Возвращаем все даны в порядке убывания
      data = Object.keys(blackBeltsByDan)
        .sort((a, b) => {
          return parseInt(b) - parseInt(a)
        })
        .map(dan => ({
          dan: dan,
          belts: blackBeltsByDan[dan]
        }))
    } else {
      data = [{
        dan: selectedDan,
        belts: blackBeltsByDan[selectedDan] || []
      }]
    }

    // Применяем дополнительные фильтры
    return data.map(({ dan, belts }) => ({
      dan,
      belts: belts.filter(filterBelt)
    })).filter(group => group.belts.length > 0)
  }

  const filteredData = getFilteredData()

  return (
    <div className="black-belts-page">
      <SEO 
        title={t('common.blackBelts')}
        description={t('blackBelts.description', 'Registry of black belt holders in SUMA International')}
        breadcrumbItems={breadcrumbItems}
      />
      <div className="container">
        <div className="black-belts-hero">
          <h1>{t('common.blackBelts')}</h1>
          <p className="black-belts-intro">
            {t('blackBelts.intro', 'The Black Belt Registry recognizes masters and advanced practitioners who have achieved the highest levels in their martial arts disciplines.')}
          </p>
        </div>

        <div className="black-belts-filters">
          <div className="filters-row">
            {/* Поиск по имени */}
            <div className="filter-group filter-search">
              <HiSearch className="filter-icon" />
              <input
                type="text"
                placeholder={t('blackBelts.searchPlaceholder', 'Поиск по имени...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="filter-input"
              />
            </div>

            {/* Фильтр по дану */}
            <div className="filter-group">
              <select
                value={selectedDan}
                onChange={(e) => setSelectedDan(e.target.value)}
                className="filter-select"
              >
                {danLevels.map(level => {
                  const label = isJapanese ? level.labelJa : (isRussian ? level.labelRu : level.labelEn)
                  return (
                    <option key={level.value} value={level.value}>
                      {label}
                    </option>
                  )
                })}
              </select>
            </div>

            {/* Фильтр по стране */}
            <div className="filter-group">
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="filter-select"
              >
                <option value="all">{t('blackBelts.allCountries', 'Все страны')}</option>
                {uniqueCountries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>

            {/* Фильтр по БИ */}
            <div className="filter-group">
              <select
                value={selectedBi}
                onChange={(e) => setSelectedBi(e.target.value)}
                className="filter-select"
              >
                <option value="all">{t('blackBelts.allBIs', 'Все БИ')}</option>
                {uniqueBIs.map(bi => (
                  <option key={bi} value={bi}>{bi}</option>
                ))}
              </select>
            </div>

            {/* Фильтр по городу аттестации */}
            <div className="filter-group">
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="filter-select"
              >
                <option value="all">{t('blackBelts.allCities', 'Все города')}</option>
                {uniqueCities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            {/* Кнопка сброса фильтров */}
            {(searchQuery || selectedCountry !== 'all' || selectedBi !== 'all' || selectedCity !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('')
                  setSelectedCountry('all')
                  setSelectedBi('all')
                  setSelectedCity('all')
                }}
                className="filter-reset-btn"
              >
                {t('blackBelts.resetFilters', 'Сбросить')}
              </button>
            )}
          </div>
        </div>

        <div className="black-belts-content">
          {filteredData.length > 0 ? (
            filteredData.map(({ dan, belts }) => {
              if (belts.length === 0) return null
              
              const danLabel = isJapanese ? `${dan}段` : (isRussian ? `${dan} Дан` : `${dan}th Dan`)
              
              return (
                <div key={dan} className="dan-section">
                  <h2 className="dan-title">{danLabel}</h2>
                  {dan === '8' && belts.length === 0 ? (
                    <p className="dan-empty">{t('blackBelts.noInfo', 'Информация отсутствует')}</p>
                  ) : (
                    <div className="belts-table">
                      <div className="belts-table-header">
                        <div className="table-col-photo"></div>
                        <div className="table-col-name">{t('blackBelts.name', 'Имя')}</div>
                        <div className="table-col-location">{t('blackBelts.location', 'Местоположение')}</div>
                        <div className="table-col-cert-number">{t('blackBelts.number', 'Номер')}</div>
                        <div className="table-col-cert-date">{t('blackBelts.date', 'Дата')}</div>
                        <div className="table-col-bi">{t('blackBelts.bi', 'БИ')}</div>
                        <div className="table-col-examiner">{t('blackBelts.examiner', 'Экзаменатор')}</div>
                        <div className="table-col-cert-city">{t('blackBelts.city', 'Город')}</div>
                        <div className="table-col-cert-photo">
                          {isMobile ? t('blackBelts.certificateShort', 'Серт.') : t('blackBelts.certificate', 'Сертификат')}
                        </div>
                      </div>
                      {belts.map(belt => {
                        const beltName = isJapanese ? belt.nameJa : (isRussian ? belt.nameRu : belt.nameEn)
                        const beltCity = belt.cityRu ? (isJapanese ? belt.cityJa : (isRussian ? belt.cityRu : belt.cityEn)) : null
                        const beltLocation = isJapanese ? belt.locationJa : (isRussian ? belt.locationRu : belt.locationEn)
                        
                        // Многоязычные поля для БИ, Экзаменатора и Города
                        const beltBi = belt.biRu || belt.biEn || belt.biJa 
                          ? (isJapanese ? belt.biJa : (isRussian ? belt.biRu : belt.biEn))
                          : (belt.bi || t('blackBelts.empty', '—'))
                        
                        const beltExaminer = belt.examinerRu || belt.examinerEn || belt.examinerJa
                          ? (isJapanese ? belt.examinerJa : (isRussian ? belt.examinerRu : belt.examinerEn))
                          : (belt.examiner || t('blackBelts.empty', '—'))
                        
                        const beltCertCity = belt.certificationCityRu || belt.certificationCityEn || belt.certificationCityJa
                          ? (isJapanese ? belt.certificationCityJa : (isRussian ? belt.certificationCityRu : belt.certificationCityEn))
                          : (belt.certificationCity || t('blackBelts.empty', '—'))
                        
                        return (
                          <div key={belt.id} className="belt-row">
                            <div className="table-col-photo">
                              {belt.photo ? (
                                <div 
                                  className="belt-photo-small clickable"
                                  onClick={() => setModalImage(belt.photo)}
                                >
                                  <img src={belt.photo} alt={beltName} />
                                </div>
                              ) : (
                                <div className="belt-photo-placeholder-small">
                                  <HiUser />
                                </div>
                              )}
                            </div>
                            <div className="table-col-name">
                              <span className="belt-name">{beltName}</span>
                            </div>
                            <div className="table-col-location">
                              <span className="belt-location">
                                {beltCity ? `${beltCity}, ${beltLocation}` : beltLocation}
                              </span>
                            </div>
                            <div className="table-col-cert-number">
                              <span className="belt-field-value">
                                {belt.certificateNumber ? (belt.certificateNumber === 'б/н' ? t('blackBelts.noNumber', 'б/н') : belt.certificateNumber) : t('blackBelts.empty', '—')}
                              </span>
                            </div>
                            <div className="table-col-cert-date">
                              <span className="belt-field-value">
                                {belt.certificationDate || t('blackBelts.empty', '—')}
                              </span>
                            </div>
                            <div className="table-col-bi">
                              <span className="belt-field-value">
                                {beltBi}
                              </span>
                            </div>
                            <div className="table-col-examiner">
                              <span className="belt-field-value">
                                {beltExaminer}
                              </span>
                            </div>
                            <div className="table-col-cert-city">
                              <span className="belt-field-value">
                                {beltCertCity}
                              </span>
                            </div>
                            <div className="table-col-cert-photo">
                              {belt.certificatePhoto ? (
                                <div 
                                  className="belt-cert-photo-small clickable"
                                  onClick={() => setModalImage(belt.certificatePhoto)}
                                >
                                  <img src={belt.certificatePhoto} alt={t('blackBelts.certificate', 'Сертификат')} />
                                </div>
                              ) : (
                                <span className="belt-field-value">{t('blackBelts.empty', '—')}</span>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })
          ) : (
            <div className="no-belts">
              <p>
                {filteredData.length === 0 && (searchQuery || selectedCountry !== 'all' || selectedBi !== 'all' || selectedCity !== 'all')
                  ? t('blackBelts.noResults', 'Черные пояса, соответствующие критериям поиска, не найдены.')
                  : t('blackBelts.noBelts', 'Черные пояса будут отображаться здесь по мере их регистрации в системе.')
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Модальное окно для просмотра изображений */}
      {modalImage && (
        <div className="image-modal" onClick={() => setModalImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="image-modal-close" 
              onClick={() => setModalImage(null)}
              aria-label={t('common.close', 'Закрыть')}
            >
              <HiX />
            </button>
            <img src={modalImage} alt={t('blackBelts.certificate', 'Сертификат')} />
          </div>
        </div>
      )}
    </div>
  )
}

export default BlackBelts
